/**
 * Keeps the short-lived `sr_media` HLS cookie fresh for the whole session.
 *
 * The server mints the cookie on join with a 30-minute lifetime
 * (MEDIA_COOKIE_TTL_SEC). Without a refresh, every segment request starts
 * failing with 401 once it expires — about 30 minutes into any film.
 *
 * Pure of React and of the network: the caller injects `refresh`, so the
 * scheduling, de-duplication and throttling are unit-testable.
 */

/** Controller returned by {@link createMediaTokenRefresher}. */
export interface MediaTokenRefresher {
  /** Begin the periodic refresh (idempotent). */
  start(): void;
  /** Stop the periodic refresh (idempotent). */
  stop(): void;
  /**
   * Refresh now (e.g. tab became visible, network came back, a segment 401'd).
   * Concurrent calls share one request; calls within `minGapMs` of the last
   * successful refresh resolve `true` without hitting the network unless
   * `force` is set. Resolves `false` if the request failed.
   */
  refreshNow(opts?: { force?: boolean }): Promise<boolean>;
}

/** Options for {@link createMediaTokenRefresher}. */
export interface MediaTokenRefresherOptions {
  /** Performs the refresh request (rejects on failure). */
  refresh: () => Promise<void>;
  /** Periodic cadence in ms; must be comfortably below the cookie TTL. */
  intervalMs: number;
  /** Skip non-forced refreshes closer together than this. Default 5000. */
  minGapMs?: number;
  /** Called when a refresh fails (the next tick retries). */
  onError?: (err: unknown) => void;
}

/** Create a refresher; nothing runs until {@link MediaTokenRefresher.start}. */
export function createMediaTokenRefresher(opts: MediaTokenRefresherOptions): MediaTokenRefresher {
  const minGapMs = opts.minGapMs ?? 5000;
  let timer: ReturnType<typeof setInterval> | null = null;
  let inflight: Promise<boolean> | null = null;
  let lastOkAt = Number.NEGATIVE_INFINITY;

  const refreshNow = (o?: { force?: boolean }): Promise<boolean> => {
    if (inflight !== null) return inflight;
    if (o?.force !== true && Date.now() - lastOkAt < minGapMs) return Promise.resolve(true);
    inflight = opts
      .refresh()
      .then(() => {
        lastOkAt = Date.now();
        return true;
      })
      .catch((err: unknown) => {
        opts.onError?.(err);
        return false;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  };

  return {
    start(): void {
      if (timer !== null) return;
      // WHY: the cookie was minted at join; the first tick is one interval
      // later, which is still well inside the TTL.
      timer = setInterval(() => {
        void refreshNow({ force: true });
      }, opts.intervalMs);
    },
    stop(): void {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    },
    refreshNow,
  };
}
